import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Save, Building2, CreditCard, Phone, FileText, Truck, Shield } from 'lucide-react';

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/admin`)
      .then(r => setS(r.data))
      .catch(() => toast.error('Не удалось загрузить настройки'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, s);
      toast.success('Сохранено');
    } catch (e) {
      toast.error('Ошибка сохранения');
    } finally { setSaving(false); }
  };

  const set = (k, v) => setS({ ...s, [k]: v });

  if (loading || !s) return <div data-testid="settings-loading">Loading…</div>;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="settings-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow mb-3">Настройки</p>
          <h1 className="h-display text-4xl md:text-5xl">Реквизиты <span className="text-[#C9A352]">компании</span></h1>
          <p className="text-[#1A1A1A]/55 mt-2">Эти данные отображаются в футере сайта и передаются в Toss Payments при оплате.</p>
        </div>
        <Button onClick={save} disabled={saving} className="cff-button-primary h-11 px-6" data-testid="settings-save">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Сохраняем…' : 'Сохранить'}
        </Button>
      </div>

      {/* Юр. информация */}
      <Card className="p-6 md:p-8" data-testid="section-legal">
        <div className="flex items-center gap-2 mb-5">
          <Building2 className="h-4 w-4 text-[#556B2F]" />
          <h3 className="eyebrow">Юридическая информация (для PG)</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="회사명 (Korean)" value={s.company_name_ko} onChange={(v) => set('company_name_ko', v)} testid="set-company-ko" />
          <Field label="Company name (English)" value={s.company_name_en} onChange={(v) => set('company_name_en', v)} testid="set-company-en" />
          <Field label="대표자 (Representative)" value={s.representative} onChange={(v) => set('representative', v)} testid="set-rep" />
          <Field label="사업자등록번호" value={s.business_reg_number} onChange={(v) => set('business_reg_number', v)} testid="set-bizreg" placeholder="000-00-00000" />
          <Field label="통신판매업 신고번호" value={s.mail_order_reg_number} onChange={(v) => set('mail_order_reg_number', v)} testid="set-mailorder" placeholder="제 0000-구청-0000 호" />
          <Field label="HACCP 인증번호" value={s.haccp_number} onChange={(v) => set('haccp_number', v)} testid="set-haccp" />
          <div className="md:col-span-2">
            <Field label="주소 (Korean)" value={s.address_ko} onChange={(v) => set('address_ko', v)} testid="set-addr-ko" />
          </div>
          <div className="md:col-span-2">
            <Field label="Address (English)" value={s.address_en} onChange={(v) => set('address_en', v)} testid="set-addr-en" />
          </div>
        </div>
      </Card>

      {/* Контакты */}
      <Card className="p-6 md:p-8" data-testid="section-contacts">
        <div className="flex items-center gap-2 mb-5">
          <Phone className="h-4 w-4 text-[#556B2F]" />
          <h3 className="eyebrow">Контакты компании</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Главный телефон" value={s.phone} onChange={(v) => set('phone', v)} testid="set-phone" />
          <Field label="Главный email" value={s.email} onChange={(v) => set('email', v)} testid="set-email" />
          <Field label="Телефон 고객센터 (CS)" value={s.cs_phone} onChange={(v) => set('cs_phone', v)} testid="set-cs-phone" />
          <Field label="Email 고객센터 (CS)" value={s.cs_email} onChange={(v) => set('cs_email', v)} testid="set-cs-email" />
          <Field label="Рабочие часы" value={s.business_hours} onChange={(v) => set('business_hours', v)} testid="set-biz-hours" />
          <Field label="CS часы работы (для футера)" value={s.customer_service_hours} onChange={(v) => set('customer_service_hours', v)} testid="set-cs-hours" />
        </div>
      </Card>

      {/* Доставка */}
      <Card className="p-6 md:p-8" data-testid="section-delivery">
        <div className="flex items-center gap-2 mb-5">
          <Truck className="h-4 w-4 text-[#556B2F]" />
          <h3 className="eyebrow">Доставка</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Field
            type="number"
            label="Стоимость доставки за коробку (KRW)"
            value={s.delivery_fee_per_box}
            onChange={(v) => set('delivery_fee_per_box', parseFloat(v) || 0)}
            testid="set-delivery-fee"
          />
        </div>
      </Card>

      {/* Банковский счёт */}
      <Card className="p-6 md:p-8" data-testid="section-bank">
        <div className="flex items-center gap-2 mb-5">
          <CreditCard className="h-4 w-4 text-[#556B2F]" />
          <h3 className="eyebrow">Банковский счёт (для перевода)</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <Field label="Банк" value={s.bank_name} onChange={(v) => set('bank_name', v)} testid="set-bank-name" placeholder="신한은행" />
          <Field label="Номер счёта" value={s.bank_account} onChange={(v) => set('bank_account', v)} testid="set-bank-acc" />
          <Field label="Получатель" value={s.bank_account_holder} onChange={(v) => set('bank_account_holder', v)} testid="set-bank-holder" />
        </div>
      </Card>

      {/* Solapi SMS */}
      <Card className="p-6 md:p-8 border-[#556B2F]/40 bg-[#F2EFE6]/30" data-testid="section-solapi">
        <div className="flex items-center gap-2 mb-5">
          <Shield className="h-4 w-4 text-[#556B2F]" />
          <h3 className="eyebrow">Solapi SMS</h3>
        </div>
        <p className="text-sm text-[#1A1A1A]/60 mb-5">
          Получите API ключи в <a href="https://console.solapi.com/credentials" target="_blank" rel="noreferrer" className="underline">Solapi → Управление ключами API</a>.
          Номер отправителя должен быть зарегистрирован в Solapi (사업자등록증 уже привязан).
        </p>
        <div className="grid md:grid-cols-1 gap-5">
          <Field label="Solapi API Key" value={s.solapi_api_key} onChange={(v) => set('solapi_api_key', v)} testid="set-solapi-key" placeholder="NCSZAR9LUN1THWWQ" />
          <div>
            <Label className="text-sm font-semibold">Solapi API Secret</Label>
            <Input
              type="password"
              value={s.solapi_api_secret || ''}
              onChange={(e) => set('solapi_api_secret', e.target.value)}
              placeholder={s.has_solapi_secret ? '••••••••••• (сохранён)' : 'XVP145QN...'}
              className="mt-2 h-11 border-[#1A1A1A]/15 focus:ring-[#556B2F] font-mono"
              data-testid="set-solapi-secret"
            />
          </div>
          <Field label="Sender (010 номер, верифицированный в Solapi)" value={s.solapi_sender} onChange={(v) => set('solapi_sender', v)} testid="set-solapi-sender" placeholder="01075694780" />
        </div>
      </Card>

      {/* Toss Payments */}
      <Card className="p-6 md:p-8 border-[#C9A352]/40 bg-[#F2EFE6]/30" data-testid="section-toss">
        <div className="flex items-center gap-2 mb-5">
          <Shield className="h-4 w-4 text-[#C9A352]" />
          <h3 className="eyebrow">Toss Payments</h3>
        </div>
        <p className="text-sm text-[#1A1A1A]/60 mb-5">
          Получите API ключи в <a href="https://app.tosspayments.com/" target="_blank" rel="noreferrer" className="underline">Toss Payments кабинете</a> (개발자 → API 키).
          Client Key используется на фронте, Secret Key — на бэкенде (никогда не показываем в HTML).
        </p>
        <div className="grid md:grid-cols-1 gap-5">
          <Field label="Toss Client Key" value={s.toss_client_key} onChange={(v) => set('toss_client_key', v)} testid="set-toss-client" placeholder="test_ck_..." />
          <div>
            <Label className="text-sm font-semibold">Toss Secret Key</Label>
            <Input
              type="password"
              value={s.toss_secret_key || ''}
              onChange={(e) => set('toss_secret_key', e.target.value)}
              placeholder={s.has_toss_secret ? '••••••••••• (сохранён, измените чтобы заменить)' : 'test_sk_...'}
              className="mt-2 h-11 border-[#1A1A1A]/15 focus:ring-[#556B2F] font-mono"
              data-testid="set-toss-secret"
            />
          </div>
        </div>
      </Card>

      <div className="flex justify-end pb-10">
        <Button onClick={save} disabled={saving} className="cff-button-primary h-12 px-8" data-testid="settings-save-bottom">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Сохраняем…' : 'Сохранить все изменения'}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, type = 'text' }) {
  return (
    <div>
      <Label className="text-sm font-semibold">{label}</Label>
      <Input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 h-11 border-[#1A1A1A]/15 focus:ring-[#556B2F]"
        data-testid={testid}
      />
    </div>
  );
}
